import { postConstruct } from 'inversify';
import { computed, ref, watch } from 'vue';

import { service } from '@/decorators/service';

const STORAGE_KEY = 'darkMode';

/**
 * DarkMode service manages the dark/light theme state.
 *
 * - Available in all environments.
 * - Persists user preference to localStorage.
 * - Applies `data-theme="dark"` attribute to <html> for CSS variable overrides.
 */
@service(DarkMode)
export class DarkMode {
  /** Whether dark mode is currently active. */
  private darkModeActive = ref(false);

  /** Whether the dark mode feature is available. */
  get isAvailable(): boolean {
    return true;
  }

  /** Reactive getter for current dark mode state. */
  get isDark() {
    return this.darkModeActive.value;
  }

  /** Computed that can be used in templates. */
  get isDarkComputed() {
    return computed(() => this.darkModeActive.value);
  }

  @postConstruct()
  initialize() {
    // Restore preference from localStorage.
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') {
      this.darkModeActive.value = true;
    }

    // Apply theme attribute on changes.
    watch(this.darkModeActive, (isDark) => {
      this.applyTheme(isDark);
      localStorage.setItem(STORAGE_KEY, String(isDark));
    }, { immediate: true });
  }

  /** Toggle dark mode on/off. */
  toggle() {
    if (!this.isAvailable) return;
    this.darkModeActive.value = !this.darkModeActive.value;
  }

  /** Explicitly set dark mode state. */
  set(isDark: boolean) {
    if (!this.isAvailable && isDark) return;
    this.darkModeActive.value = isDark;
  }

  /** Apply or remove the theme attribute on the document element. */
  private applyTheme(isDark: boolean) {
    const html = document.documentElement;
    if (isDark) {
      html.setAttribute('data-theme', 'dark');
    }
    else {
      html.removeAttribute('data-theme');
    }
  }
}
